import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuthStore } from '../../../store/auth.store';
import { useTranslation } from 'react-i18next';
import type { CashCurrentContextV1 } from '@pokerroom/types';
import { CashDepositPanel } from '../../accounting/cash/CashDepositPanel';
import { CashWorkflowPanel } from '../../accounting/cash/CashWorkflowPanel';
import { retainCashIntent, type CashIntent } from '../../accounting/cash/cashRecovery';
import { useCashScope } from '../../accounting/cash/useCashIntent';

/** One visible operation surface, backed by the original cash producers. */
export function ZipaCashForm({
  memberId,
  context,
  refresh,
}: {
  memberId: string;
  context: CashCurrentContextV1;
  refresh: () => Promise<unknown>;
}) {
  const { t } = useTranslation('member');
  const scope = useCashScope(memberId);
  const canReadMembers = useAuthStore((s) => s.allowedActions?.includes('member:read') === true);
  const canRedeem = useAuthStore(
    (s) =>
      s.allowedActions?.includes('accounting:edit') === true &&
      s.allowedActions?.includes('member:adjust_points') === true,
  );
  const references = () => {
    const result: CashIntent[] = [];
    try {
      for (const key of Object.keys(sessionStorage)) {
        if (!key.startsWith(`cash-intent-v1:${scope}:`) || key.endsWith(':history')) continue;
        const value = JSON.parse(sessionStorage.getItem(key) ?? 'null') as CashIntent;
        if (!value || !(value.scope === scope || value.scope.startsWith(`${scope}:`))) continue;
        const retained = retainCashIntent(value, value.scope, Date.now());
        if (
          retained &&
          ['ISSUE_DEPOSIT_SOURCE', 'CASH_DEPOSIT', 'CASH_ZIPA_REDEMPTION'].includes(retained.action)
        )
          result.push(retained);
      }
    } catch {
      /* Existing producer blocks posting when storage is unavailable. */
    }
    return result;
  };
  const [operation, setOperation] = useState<'DEPOSIT' | 'REDEMPTION'>(() =>
    references().some((r) => r.action === 'CASH_ZIPA_REDEMPTION' && r.stage !== 'COMMITTED')
      ? 'REDEMPTION'
      : 'DEPOSIT',
  );
  const [sessionId, setSessionId] = useState(
    () =>
      references().find((r) => r.action === 'CASH_ZIPA_REDEMPTION')?.sessionId ??
      context.activeSessionId ??
      '',
  );
  const retained = references();
  const sessions = [
    ...new Map(
      [
        ...(context.activeSessionId
          ? [{ id: context.activeSessionId, day: context.activeSessionBusinessDate }]
          : []),
        ...retained
          .filter((r) => r.action === 'CASH_ZIPA_REDEMPTION' && r.sessionId)
          .map((r) => ({ id: r.sessionId!, day: r.businessDate })),
      ].map((s) => [s.id, s]),
    ).values(),
  ];
  return (
    <section className="space-y-3" aria-label={t('zipaTab.cashTitle')}>
      <fieldset className="flex flex-wrap gap-4">
        <legend className="font-semibold">{t('zipaTab.cashTitle')}</legend>
        {(['DEPOSIT', 'REDEMPTION'] as const).map((value) => (
          <label key={value} className="min-h-11 flex gap-2 items-center">
            <input
              type="radio"
              name="zipa-cash-operation"
              checked={operation === value}
              onChange={() => setOperation(value)}
            />
            {t(value === 'DEPOSIT' ? 'ledgerDeposit' : 'zipaTab.redemption')}
          </label>
        ))}
      </fieldset>
      {retained.some((r) => !['COMMITTED', 'VOIDED'].includes(r.stage)) && (
        <p role="status">{t('zipaTab.pendingRecovery')}</p>
      )}
      {operation === 'DEPOSIT' ? (
        <CashDepositPanel memberId={memberId} context={context} refresh={refresh} />
      ) : (
        <section
          className="break-words [overflow-wrap:anywhere] space-y-3 rounded-lg border border-base-300 p-4"
          aria-label={t('zipaTab.redemption')}
        >
          <h3 className="font-semibold">{t('zipaTab.redemption')}</h3>
          {canReadMembers ? (
            <Link
              to="/members?view=zipa-balances"
              className="inline-flex min-h-11 items-center text-sm text-primary underline"
            >
              {t('balances.link')}
            </Link>
          ) : (
            <p className="text-sm text-base-content/70">{t('balances.denied')}</p>
          )}
          {!canRedeem && <p role="status">{t('balances.redemptionReadOnly')}</p>}
          {sessions.length > 1 && (
            <label className="block">
              {t('zipaTab.recoveryDay')}
              <select
                className="block min-h-11 w-full rounded border border-base-300 bg-base-100 p-2"
                value={sessionId}
                onChange={(e) => setSessionId(e.target.value)}
              >
                {sessions.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.day ?? t('zipaTab.retainedRequest')}
                  </option>
                ))}
              </select>
            </label>
          )}
          {sessionId ? (
            <CashWorkflowPanel
              key={sessionId}
              surface="redemption"
              sessionId={sessionId}
              redemptionMemberId={memberId}
            />
          ) : (
            <p role="status">{t('zipaTab.noOpenSession')}</p>
          )}
        </section>
      )}
    </section>
  );
}
